/**
 * useContacts hook - Tenant-wide contacts management
 */
import { useState, useEffect, useCallback } from 'react';
import contactsService from '../services/contactsService';
import { toast } from 'sonner';

export function useContacts(options = {}) {
  const { autoFetch = true, contactType = null } = options;
  
  const [contacts, setContacts] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (contactType) params.contact_type = contactType;
      
      const data = await contactsService.getAll(params);
      setContacts(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [contactType]);

  const fetchContactTypes = useCallback(async () => {
    try {
      const data = await contactsService.getContactTypes();
      setContactTypes(data);
    } catch (err) {
      console.error('Failed to load contact types:', err);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchContacts();
      fetchContactTypes();
    }
  }, [autoFetch, fetchContacts, fetchContactTypes]);

  const createContact = async (data) => {
    try {
      const newContact = await contactsService.create(data);
      setContacts(prev => [newContact, ...prev]);
      toast.success('Contact created');
      return newContact;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create contact';
      toast.error(message);
      throw err;
    }
  };

  const updateContact = async (id, data) => {
    try {
      const updated = await contactsService.update(id, data);
      setContacts(prev => prev.map(c => c.id === id ? updated : c));
      toast.success('Contact updated');
      return updated;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to update contact';
      toast.error(message);
      throw err;
    }
  };

  const deleteContact = async (id) => {
    try {
      await contactsService.delete(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      toast.success('Contact deleted');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to delete contact';
      toast.error(message);
      throw err;
    }
  };

  return {
    contacts,
    contactTypes,
    loading,
    error,
    refresh: fetchContacts,
    createContact,
    updateContact,
    deleteContact,
  };
}

/**
 * useProjectContacts hook - Contacts for a specific project
 */
export function useProjectContacts(projectId) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProjectContacts = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const data = await contactsService.getProjectContacts(projectId);
      setContacts(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load project contacts');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProjectContacts();
  }, [fetchProjectContacts]);

  const addContact = async (contactId, role = null, isPrimary = false) => {
    try {
      const association = await contactsService.addToProject(projectId, {
        contact_id: contactId,
        role,
        is_primary: isPrimary,
      });
      setContacts(prev => [...prev, association]);
      toast.success('Contact added to project');
      return association;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to add contact';
      toast.error(message);
      throw err;
    }
  };

  const removeContact = async (contactId) => {
    try {
      await contactsService.removeFromProject(projectId, contactId);
      setContacts(prev => prev.filter(c => c.contact_id !== contactId));
      toast.success('Contact removed from project');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to remove contact';
      toast.error(message);
      throw err;
    }
  };

  const setPrimary = async (contactId) => {
    try {
      await contactsService.setPrimaryContact(projectId, contactId);
      setContacts(prev => prev.map(c => ({
        ...c,
        is_primary: c.contact_id === contactId
      })));
      toast.success('Primary contact updated');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to set primary contact';
      toast.error(message);
      throw err;
    }
  };

  const primaryContact = contacts.find(c => c.is_primary);

  return {
    contacts,
    primaryContact,
    loading,
    error,
    refresh: fetchProjectContacts,
    addContact,
    removeContact,
    setPrimary,
  };
}

export default useContacts;
